import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen-safe bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Back button */}
        <Button variant="ghost" className="mb-6" data-testid="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} className="mr-2" />
          {t('privacy.backToHome')}
        </Button>

        <Card data-testid="privacy-policy-card">
          <CardHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-3xl font-heading">{t('privacy.title')}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{t('privacy.lastUpdated')}</p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-8">
            <div className="prose prose-sm max-w-none space-y-6">

              {/* 1. Introduction */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section1Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground"
                   dangerouslySetInnerHTML={{ __html: t('privacy.section1P1') }} />
                <p className="text-base leading-relaxed text-muted-foreground mt-3"
                   dangerouslySetInnerHTML={{ __html: t('privacy.section1P2') }} />
              </section>

              {/* 2. Data Protection Officer */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.dpoTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.dpoIntro')}
                </p>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm" dangerouslySetInnerHTML={{ __html: t('privacy.dpo_name') }} />
                  <p className="text-sm text-muted-foreground">
                    <strong>Email:</strong>{' '}
                    <a href="mailto:michellekarys@boholtrabaho.com" className="text-primary hover:underline break-words">{t('privacy.contactDPOEmail')}</a>
                  </p>
                </div>
                <p className="text-base leading-relaxed text-muted-foreground mt-3">
                  {t('privacy.dpo_role')}
                </p>
              </section>

              {/* 3. Data We Collect */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section2Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.section2Intro')}
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">{t('privacy.section2_1Title')}</h3>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_account') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_profile') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_seekers') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_employers') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_banner') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_verification') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_communications') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_socialLogin') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_referral') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_uploads') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_resume') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_peso') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_serviceListings') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_serviceRequests') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_serviceReviews') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_1_verificationDocs') }} />
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">{t('privacy.section2_2Title')}</h3>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_2_device') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_2_usage') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_2_location') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_2_cookies') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section2_2_analytics') }} />
                </ul>
              </section>

              {/* 4. Purpose of Processing */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section3Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.section3Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('privacy.section3_createAccount')}</li>
                  <li>{t('privacy.section3_connectSeekers')}</li>
                  <li>{t('privacy.section3_processApps')}</li>
                  <li>{t('privacy.section3_verifyIdentity')}</li>
                  <li>{t('privacy.section3_processPayments')}</li>
                  <li>{t('privacy.section3_sendNotifications')}</li>
                  <li>{t('privacy.section3_improveServices')}</li>
                  <li>{t('privacy.section3_complyLegal')}</li>
                  <li>{t('privacy.section3_respondInquiries')}</li>
                  <li>{t('privacy.section3_referralProgram')}</li>
                  <li>{t('privacy.section3_translateContent')}</li>
                </ul>
              </section>

              {/* 5. Legal Basis */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section4Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('privacy.section4Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-3">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section4_consent') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section4_contractual') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section4_legitimate') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section4_legal') }} />
                </ul>
              </section>

              {/* 6. Data Sharing */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section5Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.section5Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_otherUsers') }} />
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3 mb-2" dangerouslySetInnerHTML={{ __html: t('privacy.section5_serviceProviders') }} />
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground ml-4">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_onesignal') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_supabase') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_paymongo') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_clarity') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_ga') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_posthog') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_semaphore') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_anthropic') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_google_translate') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_sp_unsplash') }} />
                </ul>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-3">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_legalAuthorities') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_socialLoginProviders') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section5_partnerOrgs') }} />
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3"
                   dangerouslySetInnerHTML={{ __html: t('privacy.section5NoSell') }} />
              </section>

              {/* 7. Cross-Border Data Transfers */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.crossBorderTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.crossBorderIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.crossBorder_supabase') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.crossBorder_onesignal') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.crossBorder_clarity') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.crossBorder_ga') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.crossBorder_posthog') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.crossBorder_paymongo') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.crossBorder_anthropic') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.crossBorder_google_translate') }} />
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3">
                  {t('privacy.crossBorderNote')}
                </p>
              </section>

              {/* 8. SMS Communications */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.smsTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.smsIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('privacy.sms_dataCollected')}</li>
                  <li>{t('privacy.sms_provider')}</li>
                  <li>{t('privacy.sms_purpose')}</li>
                  <li>{t('privacy.sms_optOut')}</li>
                  <li>{t('privacy.sms_retention')}</li>
                </ul>
              </section>

              {/* 8b. AI Services (Resume Parsing) */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.aiTitle', 'AI Services (Resume Parsing)')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.aiIntro', 'When you upload a resume to your profile, we use AI to extract structured profile information automatically.')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('privacy.ai_provider', 'Resume content is sent to Anthropic (the company behind the Claude AI model) for a one-time parsing call. Anthropic does not retain or train on this data per their commercial terms.')}</li>
                  <li>{t('privacy.ai_dataShared', 'Only the resume file content is sent. We never share your resume with employers, other users, or any third party other than Anthropic for the parse call itself.')}</li>
                  <li>{t('privacy.ai_storage', 'Your resume file is stored privately on our servers (Supabase) until you delete it. You can delete it any time from your profile.')}</li>
                  <li>{t('privacy.ai_optOut', 'Resume upload is optional. You can complete your profile manually if you prefer not to use AI parsing.')}</li>
                </ul>
              </section>

              {/* 8c. Services Marketplace */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.servicesDataTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.servicesDataIntro')}
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">{t('privacy.servicesData_contactLogsTitle')}</h3>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.servicesData_contactLogs')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.servicesData_contactLogsBasis') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.servicesData_contactLogsAggregate') }} />
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">{t('privacy.servicesData_verificationDocsTitle')}</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('privacy.servicesData_verificationDocs')}
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">{t('privacy.servicesData_retentionTitle')}</h3>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.servicesData_retention')}
                </p>
                <p className="text-base leading-relaxed text-muted-foreground"
                   dangerouslySetInnerHTML={{ __html: t('privacy.servicesData_noNewParties') }} />
              </section>

              {/* 9. Authorized Partner Organizations */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.pesoTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.pesoIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('privacy.peso_aggregated')}</li>
                  <li>{t('privacy.peso_verified')}</li>
                  <li>{t('privacy.peso_purpose')}</li>
                  <li>{t('privacy.peso_consent')}</li>
                  <li>{t('privacy.peso_referral')}</li>
                  <li>{t('privacy.peso_reports')}</li>
                  <li>{t('privacy.peso_current')}</li>
                </ul>
              </section>

              {/* 10. Third-Party Authentication */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.thirdPartyAuthTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.thirdPartyAuthIntro')}
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">{t('privacy.facebookLoginTitle')}</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('privacy.facebookLoginDesc')}
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">{t('privacy.googleLoginTitle')}</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('privacy.googleLoginDesc')}
                </p>
              </section>

              {/* 11. Referral Program */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.referralTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.referralIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('privacy.referral_codes')}</li>
                  <li>{t('privacy.referral_tracking')}</li>
                  <li>{t('privacy.referral_gcash')}</li>
                  <li>{t('privacy.referral_retention')}</li>
                </ul>
              </section>

              {/* 12. Analytics and Session Recording */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.analyticsTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.analyticsIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.analytics_clarity') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.analytics_ga') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.analytics_posthog') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.analytics_meta') }} />
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3">
                  {t('privacy.analytics_consent')}
                </p>
                <p className="text-base leading-relaxed text-muted-foreground mt-2">
                  {t('privacy.analytics_optOut')}
                </p>
              </section>

              {/* 13. Data Retention */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section7Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('privacy.section7P1')}
                </p>
              </section>

              {/* 14. Your Rights */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section8Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.section8Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section8_informed') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section8_access') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section8_rectification') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section8_erasure') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section8_portability') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section8_object') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section8_complaint') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.section8_damages') }} />
                </ul>
                <p className="text-base leading-relaxed text-muted-foreground mt-3">
                  {t('privacy.section8Contact')}{' '}
                  <a href="mailto:support@boholtrabaho.com" className="text-primary hover:underline break-words">support@boholtrabaho.com</a>.
                </p>
              </section>

              {/* 15. Data Security */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section9Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('privacy.section9Intro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-3">
                  <li>{t('privacy.section9_encryption')}</li>
                  <li>{t('privacy.section9_auth')}</li>
                  <li>{t('privacy.section9_assessments')}</li>
                  <li>{t('privacy.section9_accessControls')}</li>
                  <li>{t('privacy.section9_cloudInfra')}</li>
                </ul>
              </section>

              {/* 16. Data Breach Notification */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.breachTitle')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground mb-3">
                  {t('privacy.breachIntro')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>{t('privacy.breach_npc')}</li>
                  <li>{t('privacy.breach_users')}</li>
                  <li>{t('privacy.breach_mitigation')}</li>
                  <li>{t('privacy.breach_documentation')}</li>
                </ul>
              </section>

              {/* 17. Children's Privacy */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section10Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('privacy.section10P1')}
                </p>
              </section>

              {/* 18. Updates to Policy */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section11Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('privacy.section11P1')}
                </p>
              </section>

              {/* 19. Contact Information */}
              <section>
                <h2 className="text-xl font-semibold mb-3">{t('privacy.section12Title')}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t('privacy.section12Intro')}
                </p>
                <div className="mt-3 p-4 bg-muted rounded-lg space-y-1">
                  <p className="text-sm"><strong>{t('privacy.contactName')}</strong></p>
                  <p className="text-sm text-muted-foreground">Email: <a href="mailto:support@boholtrabaho.com" className="text-primary hover:underline break-words">{t('privacy.contactEmail')}</a></p>
                  <p className="text-sm text-muted-foreground">Website: <a href="https://www.boholtrabaho.com" className="text-primary hover:underline break-words">{t('privacy.contactWebsite')}</a></p>
                  <p className="text-sm text-muted-foreground mt-2"><strong>{t('privacy.contactDPO')}</strong></p>
                  <p className="text-sm text-muted-foreground">Email: <a href="mailto:michellekarys@boholtrabaho.com" className="text-primary hover:underline break-words">{t('privacy.contactDPOEmail')}</a></p>
                </div>
              </section>

              {/* NPC Notice */}
              <section className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold mb-2 text-blue-900 dark:text-blue-100">{t('privacy.npcTitle')}</h3>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('privacy.npcDesc')}
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200 mt-2">
                  Website: <a href="https://www.privacy.gov.ph" target="_blank" rel="noopener noreferrer" className="underline">{t('privacy.npcWebsite')}</a>
                </p>
              </section>

            </div>

            {/* Data Deletion Request */}
            <div className="mt-8 p-4 bg-muted rounded-lg">
              <h3 className="font-semibold mb-2">{t('privacy.deleteDataTitle')}</h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t('privacy.deleteDataDesc')}
              </p>
              <Link to="/delete-data">
                <Button variant="outline" data-testid="delete-data-link">
                  {t('privacy.requestDeletion')}
                </Button>
              </Link>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
